<template>
  <v-container>
    <v-row>
      <v-col
        cols="12"
        md="2"
        class="mt-3"
      >
        <date-picker
          v-model="dateRange"
          range
          format="DD MMM YYYY"
          placeholder="Select Date Range"
          class="custom-datepicker"
          @change="handleChangeDateRange"
        />
      </v-col>
      <v-col
        cols="12"
        md="8"
      >
        <v-row>
          <v-col
            cols="12"
            md="3"
          >
            <v-select
              v-model="filterType"
              :items="filterOptions"
              label="Filter By"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
            v-if="filterType"
          >
            <v-autocomplete
              v-model="filterValue"
              :items="getFilterValues(filterType)"
              item-text="text"
              item-value="value"
              :label="filterType"
              multiple
              outlined
              dense
              hide-details
            >
              <template #selection="{ item, index }">
                <span v-if="index === 0">{{ item.text }}</span>
                <span
                  v-if="index === 1"
                  class="grey--text caption"
                >
                  &nbsp;(+{{ filterValue.length - 1 }} others)
                </span>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        md="10"
        class="d-flex align-center pt-0"
      >
        <v-btn
          small
          class="mr-2"
          color="primary"
          :outlined="!isDailyView"
          :depressed="isDailyView"
          @click="setDailyView"
        >
          Daily View
        </v-btn>
        <v-btn
          small
          color="primary"
          :outlined="isDailyView"
          :depressed="!isDailyView"
          @click="setHourlyView"
        >
          Hourly View
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <div 
          ref="ganttContainer" 
          style="width: 100%; height: calc(100vh - 40%);"
          class="gantt-container"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <div class="gantt-legends">
          <div
            class="legend-item"
            v-for="legend in legends"
            :key="legend.text"
          >
            <span
              class="legend-color"
              :style="{ backgroundColor: legend.color }"
            />
            {{ legend.text }}
          </div>
        </div>
      </v-col>
    </v-row>
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="gray"
        size="64"
      />
    </v-overlay>
    <v-dialog
      v-model="isOpen"
      max-width="700px"
      scrollable
      persistent
      v-if="selectedEvent"
    >
      <v-card v-if="isOpen && !isOpenService">
        <v-toolbar
          flat
          dark
          :color="selectedEvent.color"
        >
          <v-btn icon>
            <v-icon>mdi-information-outline</v-icon>
          </v-btn>
          <v-toolbar-title>{{ getColorInfo() }}</v-toolbar-title>
          <v-spacer />
          <template #extension>
            <v-tabs
              v-model="tab"
              centered
              slider-color="#ffffff"
            >
              <v-tab href="#information">
                Information Detail
              </v-tab>
              <v-tab href="#passenger">
                Passenger Info
              </v-tab>
            </v-tabs>
          </template>
        </v-toolbar>

        <v-tabs-items v-model="tab">
          <v-tab-item value="information">
            <v-card-text>
              <div
                v-for="item in infoList"
                :key="item.icon"
                class="d-flex align-center my-2"
              >
                <v-icon
                  left
                  :color="selectedEvent.color"
                  class="mr-2"
                >
                  {{ item.icon }}
                </v-icon>
                <span v-html="item.text" />
              </div>
            </v-card-text>
          </v-tab-item>

          <v-tab-item value="passenger">
            <v-simple-table class="ma-5 blue lighten-5">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Booked By</th>
                  <th>Passenger</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(passenger, index) in selectedEvent.book_detail.passenger || []"
                  :key="passenger.id"
                >
                  <td>{{ index + 1 }}</td>
                  <td>
                    {{ formatPassengerName(passenger) }}
                    <v-chip
                      x-small
                      class="ma-2"
                      color="primary"
                      dark
                      v-if="passenger.is_owner"
                    >
                      Owner
                    </v-chip>
                  </td>
                  <td v-if="isIntimap || isWSP">
                    <v-tooltip
                      bottom
                      :open-delay="500"
                    >
                      <template #activator="{ on, attrs }">
                        <v-chip
                          class="ma-1"
                          color="primary"
                          dark
                          style="font-size: 0.75rem;"
                          v-on="on"
                          v-bind="attrs"
                        >
                          {{ passenger.passenger_count }} Passenger
                        </v-chip>
                      </template>
                      <table>
                        <tbody>
                          <tr>
                            <td>
                              <p
                                v-for="pName in getPassengerList(selectedEvent.book_detail)"
                                :key="pName"
                              >
                                {{ pName }}
                              </p>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </v-tooltip>
                  </td>
                  <td v-else>
                    {{ passenger.passenger_count }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-tab-item>
        </v-tabs-items>

        <v-card-actions>
          <v-btn
            outlined
            color="primary"
            small
            class="mr-2"
            v-if="canShowEarlyReturn(selectedEvent)"
            @click="openEarlyReturnDialog(selectedEvent)"
          >
            Record early return
          </v-btn>
          <v-btn
            outlined
            color="primary"
            small
            class="mr-2"
            v-if="canShowEarlyPickup(selectedEvent)"
            @click="openEarlyPickupDialog(selectedEvent)"
          >
            Record early pickup
          </v-btn>
          <v-spacer />
          <v-btn
            text
            :color="selectedEvent.color"
            @click="isOpen = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Record early return dialog -->
    <v-dialog
      v-model="dialogEarlyReturn"
      persistent
      max-width="520"
      content-class="my-calendar-dialog"
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          Record early return
        </v-card-title>
        <v-card-text>
          <p class="mb-3 grey--text text--darken-1">
            Vehicle was returned earlier than the booked end time. Set the actual return time.
          </p>
          <v-autocomplete
            v-model="earlyReturnTime"
            :items="earlyReturnTimeOptions"
            item-text="label"
            item-value="value"
            label="Actual return time"
            outlined
            dense
            hide-details
            clearable
            placeholder="Type to search (e.g. 4 Mar or 07:30)"
          />
          <p
            v-if="earlyReturnError"
            class="caption red--text mt-2"
          >
            {{ earlyReturnError }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="dialogEarlyReturn = false; earlyReturnError = ''"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            text
            :loading="alterBookingLoading"
            @click="submitEarlyReturn"
          >
            Update booking
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Record early pickup dialog -->
    <v-dialog
      v-model="dialogEarlyPickup"
      persistent
      max-width="520"
      content-class="my-calendar-dialog"
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          Record early pickup
        </v-card-title>
        <v-card-text>
          <p class="mb-3 grey--text text--darken-1">
            Vehicle was taken earlier than the booked start time. Set the actual pickup time.
          </p>
          <v-autocomplete
            v-model="earlyPickupTime"
            :items="earlyPickupTimeOptions"
            item-text="label"
            item-value="value"
            label="Actual pickup time"
            outlined
            dense
            hide-details
            clearable
            placeholder="Type to search (e.g. 07:30)"
          />
          <p class="caption grey--text mt-1 mb-0">
            Maximum 2 hours before the booked start time.
          </p>
          <p
            v-if="earlyPickupError"
            class="caption red--text mt-2"
          >
            {{ earlyPickupError }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="dialogEarlyPickup = false; earlyPickupError = ''"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            text
            :loading="alterBookingLoading"
            @click="submitEarlyPickup"
          >
            Update booking
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isOpenService"
      max-width="700px"
      scrollable
      persistent
      v-if="selectedEvent"
    >
      <v-card>
        <v-card-text>
          <h2 class="text-center my-5">
            Vehicle Service Details
          </h2>
          <div
            v-for="item in serviceInfoList"
            :key="item.icon"
            class="d-flex align-center my-2"
          >
            <v-icon
              left
              :color="selectedEvent.color"
              class="mr-2"
            >
              {{ item.icon }}
            </v-icon>
            <span v-html="item.text" />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :color="selectedEvent.color"
            @click="isOpenService = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import gantt from 'dhtmlx-gantt/codebase/dhtmlxgantt.js'
import get from 'lodash/get';
import moment from 'moment';
import momentTz from 'moment-timezone';
import cloneDeep from 'lodash/cloneDeep';
import { getBookingDestinationText } from '@/plugins/destination';

import 'vue2-datepicker/index.css';

export default {
  components: { DatePicker },
  data() {
    const prev30Days = moment().subtract(29, 'days').startOf('day').toDate();
    const today = moment().startOf('day').toDate();

    return {
      loading: false,
      timezones: [],
      vehicles: [],
      assets: [],
      books: [],
      services: [],
      dateRange: [
        prev30Days,
        today
      ],
      dateRangeTimeout: 0,
      gantt: null,
      legends: [
        {
          text: 'Waiting For Approval',
          color: 'orange'
        },
        {
          text: 'My Booking',
          color: 'green'
        },
        {
          text: 'Ride Sharing (Rider)',
          color: 'blue'
        },
        {
          text: 'Ride Sharing (Owner)',
          color: 'cyan'
        },
        {
          text: 'Other User Booking',
          color: '#8bc34a'
        },
        {
          text: 'Booking E-bike',
          color: 'pink'
        },
        {
          text: 'Booking Other Asset',
          color: '#914f07'
        },
        {
          text: 'Vehicle Service',
          color: '#2c3e50'
        }
      ],
      filterType: null,
      filterValue: [],
      filterOptions: ["User", "Vehicle", "Origin", "Popular Destination"],
      isOpen: false,
      isOpenService: false,
      selectedEvent: null,
      selectedElement: null,
      tab: null,
      isDailyView: true,
      dialogEarlyReturn: false,
      dialogEarlyPickup: false,
      alterBookingItem: null,
      earlyReturnDateTime: null,
      earlyPickupDateTime: null,
      earlyReturnDate: '',
      earlyReturnTime: '',
      earlyPickupDate: '',
      earlyPickupTime: '',
      earlyReturnError: '',
      earlyPickupError: '',
      alterBookingLoading: false
    };
  },
  created() {
    Promise.all([
      this.$store.dispatch("LOAD_POOL_DATA"),
      this.fetchVehicles(),
      this.fetchAssets(),
      this.fetchTimezones(),
      this.fetchServices(),
    ]).then(() => {
      this.fetchBooks();
      if (this.isWollongong) {
        this.legends.push({
          text: 'Asset Group - Carpool',
          color: '#9b59b6'
        })
      }
    });
  },
  mounted() {
    if (!this.isWollongong) {
      this.legends.push({
        text: 'Booking Myki',
        color: 'purple'
      });
    }
    this.$nextTick(() => {
      this.initGantt();
    });
  },
  computed: {
    clientid() {
      return this.$store.state.clientid;
    },
    user() {
      return this.$store.state.user.user;
    },
    isWollongong() {
      return this.$store.state.clientid == 'A506MF91OINZ';
    },
    isWSP() {
      return this.$store.state.clientid == 'H1O5G7P0N3JA';
    },
    isIntimap() {
      return this.$store.state.clientid == 'DSSTWWLLIHH7';
    },
    isYarra() {
      return this.$store.state.clientid === "HCQSWYNLHF6A";
    },
    isVictrack() {
      return this.$store.state.clientid === "E4IKWG0LDBM8";
    },
    isDarwin() {
      return this.$store.state.clientid === "ED73T6OILP78";
    },
    earlyReturnHourRange() {
      if (!this.alterBookingItem || !this.alterBookingItem.book_detail) return undefined;
      const b = this.alterBookingItem.book_detail;
      const minAllowed = this.getEarlyReturnMinMoment(b);
      const startH = minAllowed ? minAllowed.hour() : moment(b.start_datetime).hour();
      const endH = moment(b.end_datetime).hour();
      return this.getHourRanges(startH, endH);
    },
    earlyReturnTimeOptions() {
      if (!this.alterBookingItem || !this.alterBookingItem.book_detail) return [];
      return this.getEarlyReturnTimeOptions(this.alterBookingItem.book_detail);
    },
    earlyPickupTimeOptions() {
      if (!this.alterBookingItem || !this.alterBookingItem.book_detail) return [];
      return this.getEarlyPickupTimeOptions(this.alterBookingItem.book_detail);
    },
    filteredBooks() {
      if (!this.filterType || !this.filterValue || this.filterValue.length === 0) {
        return this.books;
      }

      return this.books.filter(book => {
        switch (this.filterType) {
          case 'User':
            return this.filterValue.includes(`${book.first_name} ${book.last_name}`);
          case 'Vehicle':
            return this.filterValue.includes(book.book_detail.vehicle_name || 'Unknown Vehicle');
          case 'Origin':
            return this.filterValue.includes(book.book_detail.go_name || 'Unknown Origin');
          case 'Popular Destination':
            return this.filterValue.includes(getBookingDestinationText(book.book_detail.metadata) || 'Unkown Destination');
          default:
            return true;
        }
      });
    },
    // Map vehicle_id -> Set of origin names (go_name) observed in bookings
    vehicleOriginNameMap() {
      const map = new Map();
      this.books.forEach(book => {
        const vehId = book.book_detail.vehicle_id;
        const originName = book.book_detail.go_name || 'Unknown Origin';
        if (!vehId) return;
        if (!map.has(vehId)) map.set(vehId, new Set());
        map.get(vehId).add(originName);
      });
      // Add service-only vehicles so they can be matched when filtering by Origin
      this.services.forEach(service => {
        if (!map.has(service.vehicle_id)) {
          const vehicle = this.vehicles.find(v => v.id === service.vehicle_id) || {};
          const originName = vehicle.origin_name || 'Unknown Origin';
          map.set(service.vehicle_id, new Set([originName]));
        }
      });
      return map;
    },
    filteredServices() {
      // If no active filter, return all services
      if (!this.filterType || !this.filterValue || this.filterValue.length === 0) {
        return this.services;
      }
      // For User or Popular Destination filters: include services for vehicles that appear in filtered bookings
      if (this.filterType === 'User' || this.filterType === 'Popular Destination') {
        const vehiclesInFiltered = new Set(
          (this.filteredBooks || []).map(b => b.book_detail && b.book_detail.vehicle_id)
        );
        return this.services.filter(s => vehiclesInFiltered.has(s.vehicle_id));
      }
      // Filter by Vehicle: only services for selected vehicle names
      if (this.filterType === 'Vehicle') {
        return this.services.filter(s => {
          const veh = this.vehicles.find(v => v.id === s.vehicle_id) || {};
          const vehName = veh.device_name || veh.device_model || 'Unknown Vehicle';
          return this.filterValue.includes(vehName);
        });
      }
      // Filter by Origin: include services whose vehicle has at least one booking with selected origin names
      if (this.filterType === 'Origin') {
        return this.services.filter(s => {
          const origins = this.vehicleOriginNameMap.get(s.vehicle_id);
            if (!origins) return false;
            for (const name of origins) {
              if (this.filterValue.includes(name)) return true;
            }
            return false;
        });
      }
      // Other filters: leave services unchanged
      return this.services;
    },
    infoList() {
      if (!this.selectedEvent) return [];

      const e = this.selectedEvent;
      const get = this.getEventInfo;

      const base = [
        { icon: "mdi-car", text: get("vehicle") },
        {
          icon: "mdi-map-marker",
          text: `From <b>${e.book_detail.go_name}</b> to <b>${getBookingDestinationText(e.book_detail.metadata) || " - "}</b>`
        },
        { icon: "mdi-calendar-clock", text: get("date") },
        { icon: "mdi-timer-sand", text: get("duration") },
        {
          icon: "mdi-map",
          text: `Car Park Location: ${get("car_park_location")}`
        },
        {
          icon: "mdi-key",
          text: `Key Cabinet Location: ${get("cabinet_name")}`
        }
      ];

      const conditional = [
        ...(this.isVictrack || this.isDarwin
          ? [{ icon: "mdi-currency-usd", text: get("cost_code") }]
          : []),
        ...(this.isWSP || this.isIntimap
          ? [
              {
                icon: "mdi-order-alphabetical-ascending",
                text: `Work Order: ${get("work_order")}`
              },
              {
                icon: "mdi-tag-text-outline",
                text: `Purpose of Trip: ${get("purpose_of_trip")}`
              }
            ]
          : [])
      ];

      return [...base, ...conditional];
    },
    serviceInfoList() {
      if (!this.selectedEvent) return [];

      const e = this.selectedEvent;
      const vehicle = this.vehicles.find(v => v.id === e.vehicle_id) || {};

      let date = '';
      const startDate = moment(this.selectedEvent.start_date);
      const endDate = moment(this.selectedEvent.end_date);
      if (startDate.isSame(endDate, 'day')) {
        date = `${startDate.format('D MMM YYYY HH:mm A')} - ${endDate.format('HH:mm A')}`;
      } else {
        date = `${startDate.format('D MMM YYYY HH:mm A')} - ${endDate.format('D MMM YYYY HH:mm A')}`;
      }

      return [
        { icon: "mdi-car", text: vehicle.device_name || 'Unknown Vehicle' },
        { icon: "mdi-calendar-clock", text: date },
        { icon: "mdi-information-outline", text: `Note: ${e.note || 'No note provided'}` }
      ];
    }
  },
  methods: {
    getHourRanges(startHour, endHour) {
      if (startHour <= endHour) {
        return [[startHour, endHour]];
      }

      return [
        [startHour, 23],
        [0, endHour],
      ];
    },
    getPassengerList(data) {
      if (data.metadata && data.metadata.hasOwnProperty("passenger_list")) {
        return data.metadata.passenger_list;
      } else {
        return [];
      }
    },
    setDailyView() {
      this.isDailyView = true;
      if (this.gantt) {
        this.gantt.config.scale_unit = "day";
        this.gantt.config.date_scale = "%d %M";
        this.gantt.config.subscales = [];
        this.gantt.config.min_column_width = 45;
        this.gantt.render();
        this.$nextTick(() => {
          const today = moment().startOf('day').toDate();
          const gantt = this.gantt;
          const pos = gantt.posFromDate(today);
          if (typeof pos === 'number') {
            gantt.scrollTo(pos, 0);
          }
        });
      }
    },
    setHourlyView() {
      this.isDailyView = false;
      if (this.gantt) {
        this.gantt.config.scale_unit = "day";
        this.gantt.config.date_scale = "%d %M";
        this.gantt.config.subscales = [
          { unit: "hour", step: 1, date: "%H:00" }
        ];
        this.gantt.config.min_column_width = 40;
        this.gantt.render();
        this.$nextTick(() => {
          const today = moment().startOf('day');
          const gantt = this.gantt;
          const pos = gantt.posFromDate(today.toDate());
          if (typeof pos === 'number') {
            gantt.scrollTo(pos, 0);
          }
        });
      }
    },
    formatPassengerName(p) {
      return `${p.first_name}${p.last_name ? " " + p.last_name : ""}`;
    },
    getEventInfo(field) {
      if (!this.selectedEvent || !this.selectedEvent.book_detail) return '';
      switch (field) {
        case 'name':
          return `${this.selectedEvent.first_name} ${this.selectedEvent.last_name}`;
        case 'vehicle':
          if (this.selectedEvent.book_detail.vehicle_name) {
            return 'Vehicle ' + this.selectedEvent.book_detail.vehicle_name;
          } else if (this.selectedEvent.book_detail.asset_number) {
            return 'Asset ' + this.selectedEvent.book_detail.asset_number;
          } else {
            return '';
          }
        case 'date':
          const startDate = moment(this.selectedEvent.book_detail.start_datetime);
          const endDate = moment(this.selectedEvent.book_detail.end_datetime);
          if (startDate.isSame(endDate, 'day')) {
            return `${startDate.format('D MMM YYYY HH:mm A')} - ${endDate.format('HH:mm A')}`;
          } else {
            return `${startDate.format('D MMM YYYY HH:mm A')} - ${endDate.format('D MMM YYYY HH:mm A')}`;
          }
        case 'duration':
          const start = moment(this.selectedEvent.book_detail.start_datetime);
          const end = moment(this.selectedEvent.book_detail.end_datetime);
          const hours = end.diff(start, 'hours');
          if (hours < 1) {
            const minutes = end.diff(start, 'minutes');
            return `${minutes} minute${minutes > 1 ? 's' : ''}`;
          } else if (hours === 1) {
            return '1 hour';
          } else if (hours < 24) {
            return `${hours} hours`;
          } else {
            const days = Math.floor(hours / 24);
            const remainingHours = hours % 24;
            return `${days} day${days > 1 ? 's' : ''}${remainingHours > 0 ? ` ${remainingHours} hour${remainingHours > 1 ? 's' : ''}` : ''}`;
          }
        case 'cost_code':
          return this.selectedEvent.book_detail.metadata.cost_code || '';
        case 'work_order':
          return this.selectedEvent.book_detail.metadata.work_order || '';
        case 'purpose_of_trip':
          return this.selectedEvent.book_detail.metadata.purpose_of_trip || '';
        case 'car_park_location':
          if (this.selectedEvent.book_detail.vehicle_id) {
            const vehicle = this.vehicles.find(v => v.id === this.selectedEvent.book_detail.vehicle_id);
            return vehicle ? (vehicle.metadata.parking_location || '') : '';
          } else if (this.selectedEvent.book_detail.asset_id) {
            const asset = this.assets.find(a => a.id === this.selectedEvent.book_detail.asset_id);
            return asset ? (asset.metadata.parking_location || '') : '';
          }
          return ''
        case 'cabinet_name':
          if (this.selectedEvent.book_detail.vehicle_id) {
            const vehicle = this.vehicles.find(v => v.id === this.selectedEvent.book_detail.vehicle_id);
            return vehicle ? (vehicle.cabinet_name || '') : '';
          } else if (this.selectedEvent.book_detail.asset_id) {
            const asset = this.assets.find(a => a.id === this.selectedEvent.book_detail.asset_id);
            return asset ? (asset.cabinet_name || '') : '';
          }
          return '';
        default:
          return '';
      }
    },
    getFilterValues(type) {
      const options = new Set();
      switch (type) {
        case "User":
          this.books.forEach(book => options.add(`${book.first_name} ${book.last_name}`));
          break;
        case "Vehicle":
          this.books.forEach(book => options.add(book.book_detail.vehicle_name || 'Unknown Vehicle'));
          // Include service-only vehicles
          this.services.forEach(s => {
            const veh = this.vehicles.find(v => v.id === s.vehicle_id) || {};
            const vehName = veh.device_name || veh.device_model || 'Unknown Vehicle';
            options.add(vehName);
          });
          break;
        case "Origin":
          this.books.forEach(book => options.add(book.book_detail.go_name || 'Unknown Origin'));
          // Include origins from service-only vehicles via vehicle.origin_name
          const vehicleIdsWithBookings = new Set(this.books.map(b => b.book_detail.vehicle_id));
            this.services.forEach(s => {
              if (!vehicleIdsWithBookings.has(s.vehicle_id)) {
                const vehicle = this.vehicles.find(v => v.id === s.vehicle_id) || {};
                const originName = vehicle.origin_name || 'Unknown Origin';
                options.add(originName);
              }
            });
          break;
        case "Popular Destination":
          this.books.forEach(book => {
            const dest = getBookingDestinationText(book.book_detail.metadata) || 'Unknown Destination';
            if (dest) options.add(dest);
          });
          break;
      }
      return Array.from(options).map(value => ({ text: value, value }));
    },
    getColorInfo() {
      if (!this.selectedEvent) return '';
      const legend = this.legends.find(legend => legend.color === this.selectedEvent.original_color)
      if (legend) return legend.text;
      return '';
    },
    handleChangeDateRange() {
      if (this.dateRangeTimeout) {
        clearTimeout(this.dateRangeTimeout);
      }
      this.dateRangeTimeout = setTimeout(() => {
        this.fetchBooks();
        this.fetchServices();
      }, 500);
    },
    formatBookingText(start, end) {
      const mStart = moment(start);
      const mEnd = moment(end);

      const sameDay = mStart.isSame(mEnd, 'day');

      if (sameDay) {
        return `${mStart.format('D MMM HH:mm')} - ${mEnd.format('HH:mm')}`;
      } else {
        return `${mStart.format('D MMM HH:mm')} - ${mEnd.format('D MMM HH:mm')}`;
      }
    },
    formatBookingBar(start, end) {
      const mStart = moment(start);
      const mEnd = moment(end);


      if (mStart.isSame(mEnd, 'day')) {
        return `${mStart.format('HH:mm')} - ${mEnd.format('HH:mm')}`;
      } else {
        return `${mStart.format('D MMM HH:mm')} - ${mEnd.format('D MMM HH:mm')}`;
      }
    },
    // Split a booking into two when metadata indicates a second work order
    splitBookingWithOriginalEndTime(item) {
      try {
        // Ensure metadata is an object
        if (typeof item.book_detail.metadata === 'string') {
          item.book_detail.metadata = JSON.parse(item.book_detail.metadata || '{}');
        }
      } catch (e) {
        item.book_detail.metadata = {};
      }

      const md = item.book_detail.metadata || {};
      const hasSecond = md.hasOwnProperty('original_end_time') &&
                        md.hasOwnProperty('start_second_work_order') &&
                        md.hasOwnProperty('end_second_work_order') &&
                        md.hasOwnProperty('second_work_order');

      if (!hasSecond) return [item];

      // First segment: keep original start, end at original_end_time
      const first = cloneDeep(item);
      first.book_detail.end_datetime = md.original_end_time;
      // Make IDs unique for Gantt and lookups
      first.book_detail.id = `${first.book_detail.id || first.book_id}-1`;
      first.book_id = `${first.book_id}-1`;
      const firstMeta = { ...md };
      delete firstMeta.original_end_time;
      delete firstMeta.start_second_work_order;
      delete firstMeta.end_second_work_order;
      delete firstMeta.second_work_order;
      first.book_detail.metadata = firstMeta;

      // Second segment: second work order time window, update work_order
      const second = cloneDeep(item);
      second.book_detail.start_datetime = md.start_second_work_order;
      second.book_detail.end_datetime = md.end_second_work_order;
      second.book_detail.id = `${second.book_detail.id || second.book_id}-2`;
      second.book_id = `${second.book_id}-2`;
      const secondMeta = { ...md, work_order: md.second_work_order };
      delete secondMeta.original_end_time;
      delete secondMeta.start_second_work_order;
      delete secondMeta.end_second_work_order;
      delete secondMeta.second_work_order;
      second.book_detail.metadata = secondMeta;

      return [first, second];
    },
    // Process a list of bookings and split where applicable
    processBookingData(list) {
      return (list || []).reduce((acc, item) => {
        const parts = this.splitBookingWithOriginalEndTime(item);
        acc.push(...parts);
        return acc;
      }, []);
    },
    initGantt() {
      gantt.config.scale_unit = "day";
      gantt.config.date_scale = "%d %M";
      gantt.config.subscales = [];
      gantt.config.scale_height = 60;
      gantt.config.min_column_width = 45;
      gantt.config.xml_date = "%Y-%m-%d %H:%i:%s";

      const indexDays = {
        0: "sunday",
        6: "saturday",
      }

      gantt.templates.scale_cell_class = function(date){
        // 0 = Minggu, 6 = Sabtu
        if(date.getDay() === 0 || date.getDay() === 6){
          return "gantt-weekend" + " " + indexDays[date.getDay()];
        }
        return "";
      };
      gantt.templates.task_cell_class = function(item, date){
        if(date.getDay() === 0 || date.getDay() === 6){
          return "gantt-weekend" + " " + indexDays[date.getDay()];
        }
        return "";
      };
      // ...e

      gantt.templates.grid_folder = function(task) {
        if (task.id.startsWith('user-') && !task.id.includes('go-') && !task.id.includes('vehicle-')) {
          return "<span class='mdi mdi-account' style='margin-right: 5px; margin-left: 5px;'></span>";
        }
        if (task.id.includes('go-') && !task.id.includes('vehicle-')) {
          return "<span class='mdi mdi-map-marker' style='margin-right: 5px; margin-left: 5px;'></span>";
        }
        if (task.id.includes('vehicle-')) {
          return "<span class='mdi mdi-car' style='margin-right: 5px; margin-left: 5px;'></span>";
        }
        return "<span class='mdi mdi-folder' style='margin-right: 5px; margin-left: 5px;'></span>";
      };

      gantt.templates.grid_file = function(task) {
        if (task.id.startsWith('service-')) {
          return "<span class='mdi mdi-wrench' style='margin-right: 10px;'></span>";
        }
        if (task.id.startsWith('user-')) {
          return "<span class='mdi mdi-account'></span>";
        }
        if (task.id.includes('go-')) {
          return "<span class='mdi mdi-map-marker'></span>";
        }
        if (task.id.includes('vehicle-')) {
          return "<span class='mdi mdi-car'></span>";
        }
        return "<span class='mdi mdi-calendar-blank' style='margin-right: 10px;'></span>";
      };

      gantt.templates.task_class = function (start, end, task) {
        if (task.type === "project") {
          return "no-bar";
        }
        if (task.color) {
          return "event-bar-" + task.id;
        }
        return "";
      };

      const self = this;

      gantt.templates.task_text = function(start, end, task){
        if (task.id.startsWith('book-')) {
          const book = self.books.find(b => b.book_id === task.id.replace('book-', ''));
          if (book) {
            return self.formatBookingBar(
              book.book_detail.start_datetime,
              book.book_detail.end_datetime
            );
          }
        }
        if (task.id.startsWith('service-')) {
          const service = self.services.find(s => s.id === task.id.replace('service-', ''));
          return service ? service.note || 'Service' : 'Service';
        }
        return task.text || "";
      };

      gantt.templates.task_row_class = function(start, end, task) {
        if (task.color) {
          return "event-row-" + task.id;
        }
        return "";
      };

      gantt.attachEvent("onParse", function(){
        const oldStyles = document.querySelectorAll('style[data-gantt-color]');
        oldStyles.forEach(s => s.remove());
        gantt.eachTask(function(task){
          if (task.color && task.type !== 'project') {
            const style = document.createElement('style');
            style.setAttribute('data-gantt-color', task.id);
            style.innerHTML = `
              .gantt_task_line.event-bar-${task.id} { background-color: ${task.color} !important; border-color: ${task.color} !important; }
              .gantt_row.event-row-${task.id} { background-color: ${task.color}11 !important; }
            `;
            document.head.appendChild(style);
          }
        });
      });

      gantt.init(this.$refs.ganttContainer);

      gantt.config.columns = [
        { name: "text", label: "Vehicle", tree: true, width: "*" },
        { name: "booking_count", label: "Booking Count", align: "center", width: 100 }
      ];

      gantt.config.readonly = true;

      gantt.attachEvent("onTaskClick", (id, e) => {
        const task = gantt.getTask(id);

        if (task.type !== "project") {
          this.onBookingClick(e, task);
        }

        return true;
      });

      gantt.attachEvent("onParse", () => {
        gantt.eachTask(function(task) {
          if (task.type === 'project') {
            task.open = true;
          }
        });
        gantt.render();
      });

      this.gantt = gantt;
    },
    onBookingClick(e, task) {
      const isTaskContent = e.target.classList.contains('gantt_task_content');
      if (isTaskContent && task.id.startsWith('book-')) {
        const bookId = task.id.replace('book-', '');
        const book = this.books.find(b => b.book_id === bookId);
        this.selectedEvent = {
          ...task,
          ...book
        };
        this.selectedElement = e.target;
        this.isOpen = true;
        this.isOpenService = false;
      } else if (isTaskContent && task.id.startsWith('service-')) {
        const serviceId = task.id.replace('service-', '');
        const service = this.services.find(s => s.id === serviceId);
        this.selectedEvent = {
          ...task,
          ...service
        };
        this.selectedElement = e.target;
        this.isOpenService = true;
        this.isOpen = false;
      } else {
        this.isOpen = false;
        this.isOpenService = false;
        this.selectedEvent = null;
        this.selectedElement = null;
      }
    },
    getBookingColor(book) {
      let vehicleMetadata = book.book_detail.vehicle_metadata || '{}';
      
      try {
        vehicleMetadata = JSON.parse(vehicleMetadata);
      } catch (e) {
        vehicleMetadata = {};
      }

      if (vehicleMetadata == null || vehicleMetadata == undefined) {
        vehicleMetadata = {};
      }

      let colorByVehicle = null

      if (vehicleMetadata.hasOwnProperty('color_enabled')) {
        const colorEnabled = parseInt(vehicleMetadata.color_enabled, 10);
        if (colorEnabled === 1 && vehicleMetadata.hasOwnProperty('vehicle_color')) {
          colorByVehicle = vehicleMetadata.vehicle_color;
        }
      }
      
      const status = book.book_detail.status;
      const passengerList = book.book_detail.passenger || [];
      const isAssetEmpty = !book.book_detail.asset_id;
      const userId = this.user.id;

      const isSelfPassenger = passengerList.length === 1 && passengerList[0].user_id == userId;
      const isOwner = isSelfPassenger && passengerList[0].is_owner;
      const totalPassenger = passengerList.length;

      if (status == 1) {
        return {
          color: "#ff9800",
          color_by_vehicle: colorByVehicle
        };
      }

      if (totalPassenger === 1 && isSelfPassenger && isAssetEmpty) {
        return {
          color: "#4caf50",
          color_by_vehicle: colorByVehicle
        };
      }

      if (totalPassenger === 1 && !isAssetEmpty) {
        return {
          color: "#914f07",
          color_by_vehicle: colorByVehicle
        };
      }

      if (totalPassenger === 1) {
        return {
          color: "#8bc34a",
          color_by_vehicle: colorByVehicle
        };
      }

      if (totalPassenger > 1 && isSelfPassenger && isOwner) {
        return {
          color: "#00bcd4",
          color_by_vehicle: colorByVehicle
        };
      }

      return {color: "#2196f3", color_by_: colorByVehicle};
    },
    buildGanttFromBooks(books) {
  // Overloaded: prefer using filteredServices when available
  const services = this.filteredServices || this.services;
      const tasks = [];
      const goMap = new Map();
      const vehicleMap = new Map();
      const goCounter = new Map();
      const vehicleCounter = new Map();
  // Map origin label -> goId so services can reuse existing origin groups
  const originNameToGoId = new Map();

      books.forEach((book) => {
        const goId = `go-${book.book_detail.go_id}`;
        const vehicleId = `vehicle-${book.book_detail.vehicle_id}`;
        const bookId = `book-${book.book_id}`;

        if (!goMap.has(goId)) {
          const originLabel = book.book_detail.go_name || 'Unknown Origin';
          goMap.set(goId, true);
          goCounter.set(goId, 0);
          tasks.push({
            id: goId,
            text: originLabel,
            open: false,
            type: "project"
          });
          // Store mapping label -> id (first wins)
          if (!originNameToGoId.has(originLabel)) {
            originNameToGoId.set(originLabel, goId);
          }
        }

        if (!vehicleMap.has(vehicleId)) {
          vehicleMap.set(vehicleId, true);
          vehicleCounter.set(vehicleId, 0);
          tasks.push({
            id: vehicleId,
            text: book.book_detail.vehicle_name || 'Unknown Vehicle',
            parent: goId,
            open: false,
            type: "project"
          });
        }

        const color = this.getBookingColor(book);

        tasks.push({
          id: bookId,
          text: this.formatBookingText(
            book.book_detail.start_datetime,
            book.book_detail.end_datetime
          ),
          start_date: book.book_detail.start_datetime,
          end_date: book.book_detail.end_datetime,
          parent: vehicleId,
          color: color.color_by_vehicle || color.color,
          original_color: color.color,
        });

        goCounter.set(goId, goCounter.get(goId) + 1);
        vehicleCounter.set(vehicleId, vehicleCounter.get(vehicleId) + 1);
      });

      if (Array.isArray(services)) {
        services.forEach(service => {
          const vehicle = this.vehicles.find(v => v.id == service.vehicle_id) || {};
          const originLabel = vehicle.origin_name || 'Unknown Origin';
          let goId = originNameToGoId.get(originLabel);
          if (!goId) {
            // Create deterministic id from origin label
            const baseId = 'go-' + originLabel.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
            goId = baseId;
            let suffix = 1;
            while (goMap.has(goId)) {
              goId = baseId + '-' + suffix++;
            }
            goMap.set(goId, true);
            goCounter.set(goId, 0);
            tasks.push({ id: goId, text: originLabel, open: false, type: 'project' });
            originNameToGoId.set(originLabel, goId);
          }
          const vehicleId = `vehicle-${service.vehicle_id}`;
            if (!vehicleMap.has(vehicleId)) {
              vehicleMap.set(vehicleId, true);
              vehicleCounter.set(vehicleId, 0);
              tasks.push({
                id: vehicleId,
                text: vehicle.device_name || 'Unknown Vehicle',
                parent: goId,
                open: false,
                type: 'project'
              });
            }
            tasks.push({
              id: `service-${service.id}`,
              text: this.formatBookingText(service.start_date, service.end_date),
              start_date: service.start_date,
              end_date: service.end_date,
              parent: vehicleId,
              color: '#2c3e50',
              original_color: '#2c3e50',
              type: 'service'
            });
        });
      }

      tasks.forEach((task) => {
        if (task.id.startsWith("go-")) {
          task.booking_count = goCounter.get(task.id) || '';
        }
        if (task.id.startsWith("vehicle-")) {
          task.booking_count = vehicleCounter.get(task.id) || '';
        }
        if (task.type === "project") {
          delete task.start_date;
          delete task.end_date;
        }
      });

      return tasks;
    },
    fetchBooks() {
      const startDate = this.dateRange && this.dateRange[0]
        ? moment(this.dateRange[0]).format('YYYY-MM-DD')
        : moment().startOf('month').format('YYYY-MM-DD');
      const endDate = this.dateRange && this.dateRange[1]
        ? moment(this.dateRange[1]).format('YYYY-MM-DD')
        : moment().endOf('month').format('YYYY-MM-DD');

      const payload = {
        clientid: this.clientid,
        start_date: `${startDate} 00:00:00`,
        end_date: `${endDate} 23:59:59`,
        get_recurring: 1
      };

      if (this.isWSP || this.isIntimap) {
        payload.use_timezone_depot = 1;
      }

      if (this.user.type == 2 || this.user.type == 4) {
        payload.user_id = this.user.id;
      }
      
      this.loading = true;

      return this.$store
      .dispatch("API_CALL", {
        $method: "get_all_book",
        $postData: payload,
      })
      .then((response) => {
        this.books = [];
        const raw = get(response, 'data.data', []);
        // Normalize metadata and extend logic on raw items first
        const normalized = raw.map(book => {
          try {
            book.book_detail.metadata = JSON.parse(book.book_detail.metadata);
          } catch (e) {
            book.book_detail.metadata = {};
          }
          if (book.book_detail.metadata && book.book_detail.metadata.hasOwnProperty('extend')) {
            book.book_detail.end_datetime = moment(book.book_detail.end_datetime)
              .add(2, 'hours')
              .format('YYYY-MM-DD HH:mm:ss');
          }
          return book;
        });
        // Split bookings that have second work order metadata
        this.books = this.processBookingData(normalized)
          .sort((a, b) => new Date(a.book_detail.start_datetime) - new Date(b.book_detail.start_datetime));

        this.loading = false;

        this.$nextTick(() => {
          const ganttData = this.buildGanttFromBooks(this.filteredBooks);
          ganttData.forEach(task => {
            if (task.type === 'project') {
              task.open = true;
            }
          });
          this.gantt.clearAll();
          this.gantt.parse({ data: ganttData });

          this.$nextTick(() => {
            const today = moment().startOf('day');
            const start = moment(this.dateRange[0]).startOf('day');
            const end = moment(this.dateRange[1]).endOf('day');
            if (today.isBetween(start, end, null, '[]')) {
              const gantt = this.gantt;
              const pos = gantt.posFromDate(today.toDate());
              if (typeof pos === 'number') {
                gantt.scrollTo(pos, 0);
              }
            }
          });
        });
      })
      .catch((error) => {
        this.loading = false;
        this.books = [];
      });
    },
    fetchServices() {
      const startDate = this.dateRange && this.dateRange[0]
        ? moment(this.dateRange[0]).format('YYYY-MM-DD')
        : moment().startOf('month').format('YYYY-MM-DD');
      const endDate = this.dateRange && this.dateRange[1]
        ? moment(this.dateRange[1]).format('YYYY-MM-DD')
        : moment().endOf('month').format('YYYY-MM-DD');
      return this.$store
      .dispatch("API_CALL", {
        $method: "get_vehicle_services",
        $postData: {
          clientid: this.clientid,
          start_date: startDate,
          end_date: endDate,
        },
      })
      .then((response) => {
        this.services = get(response, 'data.data', []);
        // Re-render Gantt when services updated
        if (this.gantt) {
          const ganttData = this.buildGanttFromBooks(this.filteredBooks);
          ganttData.forEach(task => { if (task.type === 'project') task.open = true; });
          this.gantt.clearAll();
          this.gantt.parse({ data: ganttData });
        }
      });
    },
    fetchTimezones() {
      return this.$store
      .dispatch("API_CALL", {
        $method: "get_timezone_from_file",
      })
      .then((response) => {
        this.timezones = get(response, 'data.data.windowsZones.mapTimezones.mapZone', [])
      });
    },
    fetchVehicles() {
      return this.$store
      .dispatch("API_CALL", {
        $method: "get_vehicles",
        $postData: {
          clientid: this.clientid,
        },
      })
      .then((response) => {
        this.vehicles = get(response, 'data.data', []);
      });
    },
    fetchAssets() {
      return this.$store
      .dispatch("API_CALL", {
        $method: "get_assets",
        $postData: {
          clientid: this.clientid,
        },
      })
      .then((response) => {
        this.assets = get(response, 'data.data', []);
      });
    },
    parseDepotTime(datetimeStr, geofenceId) {
      if ((this.isIntimap || this.isWSP) && geofenceId) {
        const ianaTz = this.getDepotIanaTimezone(geofenceId);
        if (ianaTz) {
          return momentTz.tz(moment(datetimeStr).format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm', ianaTz);
        }
      }
      return moment(datetimeStr);
    },
    pastCondition(bookDetail) {
      if (!bookDetail) return false;
      const now = moment().unix();
      const end = this.parseDepotTime(bookDetail.end_datetime, bookDetail.start_geofence_id).unix();
      return end > now;
    },
    bookingHasStarted(bookDetail) {
      if (!bookDetail || !bookDetail.start_datetime) return false;
      const start = this.parseDepotTime(bookDetail.start_datetime, bookDetail.start_geofence_id);
      return moment().isSameOrAfter(start);
    },
    getEarlyReturnMinMoment(bookDetail) {
      if (!bookDetail || !bookDetail.start_datetime) return null;
      const start = this.parseDepotTime(bookDetail.start_datetime, bookDetail.start_geofence_id);
      const minFromStart = start.clone().add(30, 'minutes');
      return moment.max(moment(), minFromStart);
    },
    floorToHalfHour(datetimeMoment) {
      const m = datetimeMoment.clone().seconds(0).milliseconds(0);
      m.minutes(m.minutes() >= 30 ? 30 : 0);
      return m;
    },
    ceilToHalfHour(datetimeMoment) {
      const m = datetimeMoment.clone().seconds(0).milliseconds(0);
      const minute = m.minutes();
      if (minute === 0 || minute === 30) return m;
      if (minute < 30) {
        m.minutes(30);
        return m;
      }
      return m.add(1, 'hour').minutes(0);
    },
    getEarlyReturnTimeOptions(bookDetail) {
      if (!bookDetail || !bookDetail.start_datetime || !bookDetail.end_datetime) return [];
      const start = this.parseDepotTime(bookDetail.start_datetime, bookDetail.start_geofence_id);
      const end = this.parseDepotTime(bookDetail.end_datetime, bookDetail.start_geofence_id);
      const now = moment();

      const isMultiDay = !start.isSame(end, 'day');

      if (now.isAfter(end)) {
        const first = this.ceilToHalfHour(start);
        const last = this.floorToHalfHour(end);
        const options = [];
        const cursor = first.clone();
        while (cursor.isSameOrBefore(last)) {
          options.push({
            label: isMultiDay ? cursor.format('D MMM HH:mm') : cursor.format('HH:mm'),
            value: cursor.format('YYYY-MM-DD HH:mm'),
          });
          cursor.add(30, 'minutes');
        }
        return options;
      }

      const minFromStart = start.clone().add(30, 'minutes');
      const nowSlot = this.floorToHalfHour(now);
      if (nowSlot.isBefore(minFromStart) || nowSlot.isSameOrAfter(end)) {
        return [];
      }

      const first = this.ceilToHalfHour(minFromStart);
      const options = [];
      const cursor = first.clone();
      while (cursor.isSameOrBefore(nowSlot)) {
        options.push({
          label: isMultiDay ? cursor.format('D MMM HH:mm') : cursor.format('HH:mm'),
          value: cursor.format('YYYY-MM-DD HH:mm'),
        });
        cursor.add(30, 'minutes');
      }
      return options;
    },
    getEarlyPickupTimeOptions(bookDetail) {
      if (!bookDetail || !bookDetail.start_datetime) return [];
      const start = this.parseDepotTime(bookDetail.start_datetime, bookDetail.start_geofence_id);
      const now = moment();
      if (now.isSameOrAfter(start)) {
        return [];
      }

      const twoHoursBefore = start.clone().subtract(2, 'hours');
      const first = this.ceilToHalfHour(twoHoursBefore);
      const upper = this.floorToHalfHour(now);
      if (upper.isBefore(first)) {
        return [];
      }

      const options = [];
      const cursor = first.clone();
      while (cursor.isSameOrBefore(upper) && cursor.isBefore(start)) {
        options.push({
          label: cursor.format('HH:mm'),
          value: cursor.format('YYYY-MM-DD HH:mm'),
        });
        cursor.add(30, 'minutes');
      }
      return options;
    },
    withinEarlyReturnWindow(bookDetail) {
      return this.getEarlyReturnTimeOptions(bookDetail).length > 0;
    },
    withinEarlyPickupWindow(bookDetail) {
      return this.getEarlyPickupTimeOptions(bookDetail).length > 0;
    },
    hasEarlyReturnTag(bookDetail) {
      if (!bookDetail || !bookDetail.metadata) return false;
      try {
        const meta = typeof bookDetail.metadata === 'string' ? JSON.parse(bookDetail.metadata) : bookDetail.metadata;
        return meta && (meta.early_return === true || meta.early_return === 'true');
      } catch (e) {
        return false;
      }
    },
    hasEarlyPickupTag(bookDetail) {
      if (!bookDetail || !bookDetail.metadata) return false;
      try {
        const meta = typeof bookDetail.metadata === 'string' ? JSON.parse(bookDetail.metadata) : bookDetail.metadata;
        return meta && (meta.early_pickup === true || meta.early_pickup === 'true');
      } catch (e) {
        return false;
      }
    },
    canShowEarlyReturn(event) {
      if (!event || !event.book_detail) return false;
      const b = event.book_detail;
      return (this.isIntimap || this.isWSP)
        && this.pastCondition(b)
        && !event.is_second_booking
        && b.join_with == null
        && this.withinEarlyReturnWindow(b)
        && !this.hasEarlyReturnTag(b);
    },
    canShowEarlyPickup(event) {
      if (!event || !event.book_detail) return false;
      const b = event.book_detail;
      return (this.isIntimap || this.isWSP)
        && this.pastCondition(b)
        && !event.is_second_booking
        && b.join_with == null
        && this.withinEarlyPickupWindow(b)
        && !this.hasEarlyPickupTag(b);
    },
    openEarlyReturnDialog(event) {
      this.alterBookingItem = event;
      const b = event.book_detail;
      if (!b) return;
      const geofenceId = b.start_geofence_id;
      const bookingEndMoment = this.parseDepotTime(b.end_datetime, geofenceId);
      const options = this.getEarlyReturnTimeOptions(b);
      const selectedOption = options.length ? options[options.length - 1].value : '';
      const selected = selectedOption ? this.parseDepotTime(selectedOption, geofenceId) : bookingEndMoment.clone().subtract(30, 'minutes');
      this.earlyReturnDateTime = selected.toDate();
      this.earlyReturnDate = selected.format('YYYY-MM-DD');
      this.earlyReturnTime = selectedOption;
      this.earlyReturnError = options.length ? '' : 'No valid early return time can be selected right now.';
      this.isOpen = false;
      this.dialogEarlyReturn = true;
    },
    openEarlyPickupDialog(event) {
      this.alterBookingItem = event;
      const b = event.book_detail;
      if (!b) return;
      const geofenceId = b.start_geofence_id;
      const bookingStartMoment = this.parseDepotTime(b.start_datetime, geofenceId);
      const options = this.getEarlyPickupTimeOptions(b);
      const selectedOption = options.length ? options[options.length - 1].value : '';
      const selected = selectedOption ? this.parseDepotTime(selectedOption, geofenceId) : bookingStartMoment.clone().subtract(30, 'minutes');
      this.earlyPickupDateTime = selected.toDate();
      this.earlyPickupDate = selected.format('YYYY-MM-DD');
      this.earlyPickupTime = selectedOption;
      this.earlyPickupError = options.length ? '' : 'No valid early pickup time can be selected right now.';
      this.isOpen = false;
      this.dialogEarlyPickup = true;
    },
    getDepotIanaTimezone(geofenceId) {
      if (!geofenceId) return null;
      const poolData = this.$store.getters.getPoolData || [];
      const origin = poolData.find(o => o.Id && o.Id.toString() === geofenceId.toString());
      if (!origin) return null;
      const tz = get(origin, 'metadata.timezone.timeZoneId');
      if (!tz) return null;
      const matched = this.timezones.find(t => {
        if (typeof t.type === 'string') return t.type === tz;
        if (Array.isArray(t.type)) return t.type.includes(tz);
        return false;
      });
      return matched ? (typeof matched.type === 'string' ? matched.type : matched.type[0]) : null;
    },
    depotLocalToUtc(localDatetime, geofenceId) {
      const ianaTz = this.getDepotIanaTimezone(geofenceId);
      if (ianaTz) {
        return momentTz.tz(moment(localDatetime).format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm', ianaTz).utc().format('YYYY-MM-DD HH:mm');
      }
      return moment(localDatetime).format('YYYY-MM-DD HH:mm');
    },
    submitEarlyReturn() {
      if (!this.alterBookingItem || !this.alterBookingItem.book_detail) return;
      if (!this.earlyReturnTime) {
        this.earlyReturnError = 'Please select a time.';
        return;
      }
      const b = this.alterBookingItem.book_detail;
      const bookId = b.original_id || b.id;
      const geofenceId = b.start_geofence_id;
      const start = moment(b.start_datetime).format('YYYY-MM-DD HH:mm');
      const selectedInput = this.earlyReturnTime && this.earlyReturnTime.includes(' ')
        ? this.earlyReturnTime
        : `${this.earlyReturnDate} ${this.earlyReturnTime}`;
      const selectedMoment = this.parseDepotTime(selectedInput, geofenceId);
      const bookingStartMoment = this.parseDepotTime(b.start_datetime, geofenceId);
      const bookingEndMoment = this.parseDepotTime(b.end_datetime, geofenceId);
      const newEnd = selectedMoment.format('YYYY-MM-DD HH:mm');
      if (selectedMoment.isAfter(bookingEndMoment)) {
        this.earlyReturnError = 'Actual return time must be earlier than the booked end time.';
        return;
      }
      if (selectedMoment.isBefore(bookingStartMoment)) {
        this.earlyReturnError = 'Actual return time cannot be before the booking start time.';
        return;
      }
      this.earlyReturnError = '';
      this.alterBookingLoading = true;
      let existingMetadata = {};
      try {
        existingMetadata = typeof b.metadata === 'string' ? JSON.parse(b.metadata || '{}') : (b.metadata || {});
      } catch (e) {
        existingMetadata = {};
      }
      const fallbackOriginalStart = (this.isIntimap || this.isWSP)
        ? this.depotLocalToUtc(b.start_datetime, geofenceId)
        : start;
      const fallbackOriginalEnd = (this.isIntimap || this.isWSP)
        ? this.depotLocalToUtc(b.end_datetime, geofenceId)
        : moment(b.end_datetime).format('YYYY-MM-DD HH:mm');
      const earlyReturnData = {
        id: String(bookId).split('-')[0],
        start_datetime: (this.isIntimap || this.isWSP) ? this.depotLocalToUtc(b.start_datetime, geofenceId) : start,
        end_datetime: (this.isIntimap || this.isWSP) ? this.depotLocalToUtc(selectedInput, geofenceId) : newEnd,
        metadata: {
          ...existingMetadata,
          early_return: true,
          original_booking_start: existingMetadata.original_booking_start || fallbackOriginalStart,
          original_booking_end: existingMetadata.original_booking_end || fallbackOriginalEnd,
        },
      };
      this.$store.dispatch('API_CALL', {
        $method: 'update_book',
        $postData: {
          data: earlyReturnData,
        },
      }).then((response) => {
        this.alterBookingLoading = false;
        this.dialogEarlyReturn = false;
        this.alterBookingItem = null;
        if (response && response.data && response.data.code == 200) {
          this.$swal({ icon: 'success', title: 'Booking updated', text: 'End time updated to reflect early return.' });
          this.fetchBooks();
        } else {
          this.$swal({ icon: 'error', title: get(response, 'data.data.message', 'Update failed') });
        }
      }).catch(() => {
        this.alterBookingLoading = false;
        this.$swal({ icon: 'error', title: 'Update failed' });
      });
    },
    submitEarlyPickup() {
      if (!this.alterBookingItem || !this.alterBookingItem.book_detail) return;
      if (!this.earlyPickupTime) {
        this.earlyPickupError = 'Please select a time.';
        return;
      }
      const b = this.alterBookingItem.book_detail;
      const bookId = b.original_id || b.id;
      const geofenceId = b.start_geofence_id;
      const selectedInput = this.earlyPickupTime && this.earlyPickupTime.includes(' ')
        ? this.earlyPickupTime
        : `${this.earlyPickupDate} ${this.earlyPickupTime}`;
      const selectedMoment = this.parseDepotTime(selectedInput, geofenceId);
      const bookingStartMoment = this.parseDepotTime(b.start_datetime, geofenceId);
      const bookingEndMoment = this.parseDepotTime(b.end_datetime, geofenceId);
      const newStart = selectedMoment.format('YYYY-MM-DD HH:mm');
      const end = moment(b.end_datetime).format('YYYY-MM-DD HH:mm');
      if (selectedMoment.isSameOrAfter(bookingStartMoment)) {
        this.earlyPickupError = 'Actual pickup time must be earlier than the booked start time.';
        return;
      }
      const twoHoursBeforeStart = bookingStartMoment.clone().subtract(2, 'hours');
      if (selectedMoment.isBefore(twoHoursBeforeStart)) {
        this.earlyPickupError = 'Actual pickup time can be at most 2 hours before the booked start time.';
        return;
      }
      if (selectedMoment.isAfter(bookingEndMoment)) {
        this.earlyPickupError = 'Actual pickup time cannot be after the booking end time.';
        return;
      }
      this.earlyPickupError = '';
      this.alterBookingLoading = true;
      let existingMetadata = {};
      try {
        existingMetadata = typeof b.metadata === 'string' ? JSON.parse(b.metadata || '{}') : (b.metadata || {});
      } catch (e) {
        existingMetadata = {};
      }
      const fallbackOriginalStart = (this.isIntimap || this.isWSP)
        ? this.depotLocalToUtc(b.start_datetime, geofenceId)
        : moment(b.start_datetime).format('YYYY-MM-DD HH:mm');
      const earlyPickupData = {
        id: String(bookId).split('-')[0],
        start_datetime: (this.isIntimap || this.isWSP) ? this.depotLocalToUtc(selectedInput, geofenceId) : newStart,
        end_datetime: (this.isIntimap || this.isWSP) ? this.depotLocalToUtc(b.end_datetime, geofenceId) : end,
        metadata: {
          ...existingMetadata,
          early_pickup: true,
          original_booking_start: existingMetadata.original_booking_start || fallbackOriginalStart,
        },
      };
      this.$store.dispatch('API_CALL', {
        $method: 'update_book',
        $postData: {
          data: earlyPickupData,
        },
      }).then((response) => {
        this.alterBookingLoading = false;
        this.dialogEarlyPickup = false;
        this.alterBookingItem = null;
        if (response && response.data && response.data.code == 200) {
          this.$swal({ icon: 'success', title: 'Booking updated', text: 'Start time updated to reflect early pickup.' });
          this.fetchBooks();
        } else {
          this.$swal({ icon: 'error', title: get(response, 'data.data.message', 'Update failed') });
        }
      }).catch(() => {
        this.alterBookingLoading = false;
        this.$swal({ icon: 'error', title: 'Update failed' });
      });
    }
  },
  watch: {
    filterType() {
      this.filterValue = [];
    },
    filterValue() {
      if (this.gantt) {
        const ganttData = this.buildGanttFromBooks(this.filteredBooks);
        ganttData.forEach(task => {
          if (task.type === 'project') {
            task.open = true;
          }
        });
        this.gantt.clearAll();
        this.gantt.parse({ data: ganttData });

        this.$nextTick(() => {
          const today = moment().startOf('day');
          const start = moment(this.dateRange[0]).startOf('day');
          const end = moment(this.dateRange[1]).endOf('day');
          if (today.isBetween(start, end, null, '[]')) {
            const gantt = this.gantt;
            const pos = gantt.posFromDate(today.toDate());
            if (typeof pos === 'number') {
              gantt.scrollTo(pos, 0);
            }
          }
        });
      }
    }
  }
}
</script>

<style lang="scss">
  @import '~dhtmlx-gantt/codebase/dhtmlxgantt.css';

  .custom-datepicker {
    width: 100%;

    input {
      border: 1px solid rgba(0, 0, 0, 0.38);
      padding: 19px 10px;
      border-radius: 4px;
      width: 100%;
      font-family: inherit;
    }
  }

  .gantt_task_line.no-bar {
    display: none !important;
  }

  .custom-datepicker {
    width: 100%;
    input {
      border: 1px solid rgba(0, 0, 0, 0.38);
      padding: 19px 10px;
      border-radius: 4px;
      width: 100%;
      font-family: inherit;
    }
  }

  .gantt-legends {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    font-size: 14px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .legend-color {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    border: 1px solid #ccc;
  }

  .gantt_grid_head_cell,
  .gantt_scale_cell {
    color: #454545 !important;
  }

  .gantt-weekend.saturday {
    background-color: #fff0f0 !important;
  }

  .gantt-weekend.sunday {
    background-color: #d1d2ff !important;
  }
</style>
<style>
.my-calendar-dialog {
  overflow: visible !important;
}
</style>
